import type { TargetGroupId } from "../outreach/panel/types";
import { SegmentGroupId, SegmentId } from "../responses/segments/types";
import type { CurrentQuestionInfo } from "./client_logic/currentQuestion";
import type { HistoryInfo } from "./client_logic/history";
import type { ForceMediaPlayingInfo } from "./client_logic/mediaPlaying";
import type { RedirectInfo } from "./client_logic/redirect";
import { ScreenerStatusFull } from "./client_logic/screener";
import type { ConceptSamplingFunction } from "./conceptSampling";
import type { QuestionType } from "./questionTypes";
import { RecordingEvent } from "./recording";
import type {
  FormSubmissionId,
  RecruitmentAudiencePlanId,
} from "@/backend/db/drizzle/types/idTypes";
import type {
  RecordingDiagnostics,
  ResponseRecording,
} from "@/components/interview2/recorder/recordingDiagnostics";
import type { STREAMABLE_MODEL } from "@/utils/constants";
import type {
  AudioLanguage,
  Database,
  OrganizationId,
  RejectReason,
  ResponseId,
  ScreenerStatus,
  SurveyConfig,
  SurveyId,
  SurveyRevisionId,
  TranscriptionWord,
  DistributiveOmit,
  UnionWithUndefined,
  UserId,
} from "@/utils/types";
import type { MutableRefObject } from "react";

/** Model and prompt version from /api/chat stream headers, for tracking. */
export type LLMModelAndPromptInfo = {
  model: STREAMABLE_MODEL;
  promptVersion: number;
};

// Preview Interviewer pill selection. Variant pins a blind tree name; model/prompt resolve from the FE map.
export type PreviewLlmSelection =
  | { mode: "default" }
  | { mode: "variant"; variantKey: string }
  | { mode: "custom"; model: STREAMABLE_MODEL; promptVersion: number };

// General types

export type InterviewBlockId = string & { readonly __brand: unique symbol };
export type InterviewItemTemplateId = string & {
  readonly __brand: unique symbol;
};
export type ConceptId = string & { readonly __brand: unique symbol };
// unique id = uuidv5(templateId, conceptId)
export type InterviewItemId = string & {
  readonly __brand: unique symbol;
};
export type OptionId = string & { readonly __brand: unique symbol };
// Stable id for one conjoint attribute (e.g. "Price", "Storage"). Levels under
// an attribute reuse `OptionId`, so every level already has a stable global id.
export type ConjointAttributeId = string & { readonly __brand: unique symbol };
// Hierarchical readable id for a media slot, e.g. "Q3.M1", "C2.M2". Stored on
// each MediaDef in editor state; stamped by assignMediaReadableIds at the end
// of applyChanges and on load via convertFromStudy. Stripped on save by
// stripMediaReadableIds (convertToStudy.ts): editor-only derived state, not
// persisted. The LLM echoes it on UPDATE_ITEM/UPDATE_CONCEPT to signal which
// old slot to carry per-language variants forward from. Branded to keep it
// from being confused with other LLM ids in lookups.
export type LLMMediaSlotID = string & { __brand: "SimplifiedMediaSlotID" };

// ############### InterviewItem ###############

export type InterviewItemType =
  | "question"
  | "statement"
  | "interview_end"
  | "video_quality_check"
  | "identification_collection"
  | "unsampled_concept_block"
  | "form_question";

// Narrower than MediaDef on purpose, one image per option rather than an
// array, since an answer choice is one thing compared at a glance.
export type OptionMedia = {
  type: "image";
  name: string;
  url: string;
  // Absent crops to fill (older images); new pictures are saved as "contain".
  fit?: "cover" | "contain";
};

export type Option = {
  id: OptionId;
  text: string;
  pinned?: boolean;
  exclusiveOption?: boolean;
  // `text` stays required even when media is set: it is the stable key that
  // reports, exports, CSVs, the interviewer prompt, and MaxDiff results all
  // render. Whether it is shown to the respondent is a display concern.
  media?: OptionMedia;
};
export type MatrixRow = { id: OptionId; text: string };
// Per-language override for a url_embed. Lives on base only;
// translator strips byLanguage from overlay embeds.
export type EmbedVariant = {
  url: string;
  proxyUrl?: string;
};
export type EmbedDef =
  | {
      type: "concept_embed";
    }
  | {
      type?: "url_embed";
      url: string;
      proxyUrl?: string; // if set, overrides the url, but url is what is shown to the user
      watermark?: boolean;
      byLanguage?: Partial<Record<AudioLanguage, EmbedVariant>>;
    };
export type InterviewItemTemplateBase = {
  id: InterviewItemTemplateId;
  text: string;
  /**
   * @deprecated Do not access directly - use OptionsResolverUtils.getOptions() or
   * getOptionsUnsafe() for authoring contexts. Direct access doesn't handle carry forward.
   */
  choices?: Option[];
  media?: MediaDefOrPlaceholder[];
  embed?: EmbedDef;
  conditional?: ConditionalDef;
};

// Per-language override for a single media slot. Same authorial fields as
// MediaDef but no nested byLanguage (variants are flat). show_controls is
// intentionally omitted: variants override visual content per language;
// playback affordances (force_watching, show_controls) are properties of the
// slot, not the localized asset. The LLM-write schema and UI both reflect
// this; mergeSlotVariant carries show_controls forward from the base slot.
export type MediaVariant = {
  name: string;
  url: string;
} & (
  | {
      type: "image";
    }
  | {
      type: "video";
      force_watching?: boolean;
    }
);

// ----------- QuestionItem ----------------
export type MediaDef = {
  name: string;
  url: string;
  // Hierarchical id (`Q3.M1`, `C2.M2`) stamped by assignMediaReadableIds at
  // the end of applyChanges and on load in convertFromStudy. Stripped on save
  // by stripMediaReadableIds. Optional because mid-batch state (after
  // ADD_ITEMS but before the end-of-batch assign pass) is not yet stamped;
  // readers fall back to inline compute in that window. Stored as plain
  // string (not branded `LLMMediaSlotID`) so Zod-parsed values from
  // UPDATE_ITEM/UPDATE_CONCEPT assign without casts at the boundary; the
  // brand is reapplied at Map-key sites in pairMediaByReadableId.
  // TODO(future): move this editor-only handle to an editor-side fork the way
  // form fields use CreationStateFormField, so interview types stay clean.
  mediaReadableId?: string;
  // Per-language overrides. Lives only on base SurveyData; the translator
  // strips it from each MediaDef on translation overlays. When a language has
  // no entry, the renderer falls back to the base slot's url/type/etc.
  byLanguage?: Partial<Record<AudioLanguage, MediaVariant>>;
  watermark?: boolean;
  // Label shown under the media so participants can tell several apart, e.g.
  // "Video A" when a later ranking lists the videos by name.
  caption?: string;
} & (
  | {
      type: "image";
      // Seconds the image stays visible once the respondent reveals it, after
      // which it is covered for the rest of the item. Unset means no limit.
      view_time_limit_seconds?: number;
    }
  | {
      type: "video";
      force_watching?: boolean;
      show_controls?: boolean; // in case of force watching, show controls like fullscreen
      // The respondent gets one playthrough, after which the video is covered
      // for the rest of the item. Unset means they can replay it freely.
      single_view?: boolean;
    }
);
export type MediaDefOrPlaceholder =
  | MediaDef
  | {
      type: "concept-placeholder"; // will be replaced with all media items of current concept
      force_watching?: boolean;
      show_controls?: boolean;
      single_view?: boolean;
      // Per question, not per concept: the same concept media is shown by
      // several questions in a block, and each decides its own limit.
      view_time_limit_seconds?: number;
    };

export type OptionSelectionMatching =
  | { matchingCriteria: "mustSelect" | "mustNotSelect" }
  | { matchingCriteria: "mustSelectAtLeast"; minCount: number };

export type ConditionalDefSingle =
  | {
      type: "searchParam";
      parameter: string;
      value: string;
    }
  | {
      // TODO(soon): does not work well with translations, change to use option id instead in the future
      type: "answerString"; // looks at the text answer and selected options
      itemId: InterviewItemId; // needs to be item not template as question can appear for each concept
      answer: string;
      matchingCriteria?: "exact" | "notEquals" | "contains"; // contains is not used atm
    }
  | {
      type: "selectedOptions";
      itemId: InterviewItemId;
      matchingCriteria: "mustSelect" | "mustNotSelect"; // needs to select at least one or select none of the choices
      choices: OptionId[];
    }
  | ({
      type: "selectedTemplateOptions";
      templateId: InterviewItemTemplateId;
      choices: OptionId[];
    } & OptionSelectionMatching)
  | {
      type: "concept";
      concepts: ConceptId[];
    }
  | {
      type: "target_group";
      target_group_ids: TargetGroupId[];
    }
  | {
      type: "audience";
      audience_ids: RecruitmentAudiencePlanId[];
    }
  | {
      type: "segment";
      segmentGroupId: SegmentGroupId;
      segmentIds: SegmentId[];
    };

// Recursive type definition
// In the UI, only one conjunction or disjunction is allowed (no nesting)
// The nesting is needed to merge concept conditionals with a item conditional that is a disjunction
export type ConditionalDef =
  | ConditionalDefSingle
  | { type: "conjunction"; items: ConditionalDef[] }
  | { type: "disjunction"; items: ConditionalDef[] };

export type { QuestionType } from "./questionTypes";
export type FollowUpStyle = "none" | "light" | "medium" | "heavy";

export const PREFERRED_INPUT_OPTIONS = [
  "text",
  "voice",
  "screenRecording",
  "none",
] as const;
export type PreferredInput = (typeof PREFERRED_INPUT_OPTIONS)[number];

export type TaskConfig = {
  destinationSource?: "concept" | "previous" | "custom" | null;
  url?: string;
  pipEnabled?: boolean;
};

export type QuestionItem = InterviewItemTemplateBase & {
  type: "question";
  questionType: QuestionType;
  addInstructions?: string;
  preferredInput?: PreferredInput;
  allow_voice?: boolean;
  followUp?: FollowUpStyle;
  required?: boolean;
  // Participants may skip it. v2 only: v1 reads required + config.disable_skip.
  isSkippable?: boolean;
  disallow_other?: boolean; // for multiple choice questions
  multi_select?: boolean;
  min_select?: number; // for multiselect
  max_select?: number; // for multiselect
  randomize_choices?: boolean; // for multiple choice/multi-select questions
  carryForwardFrom?: InterviewItemTemplateId; // carry forward options from another question
  rows?: MatrixRow[]; // for matrix questions
  matrixDisplay?: MatrixDisplay; // for matrix questions: how the respondent answers it
  metric?: string; // for max_diff questions: fills "Most ___" / "Least ___"
  itemsPerScreen?: MaxDiffPerScreen; // for max_diff questions: subset size shown per screen (default 4)
  // ----- conjoint (CBC) -----
  attributes?: ConjointAttribute[]; // for conjoint: the attributes x levels grid
  profilesPerScreen?: 2 | 3 | 4; // for conjoint: profiles shown per choice task (default 3)
  numTasks?: number; // for conjoint: choice tasks per respondent (default 10)
  includeNoneOption?: boolean; // for conjoint: offer "none of these" on each task
  priceAttributeId?: ConjointAttributeId; // for conjoint: marks the price attribute (enables WTP)
  // Turns the question's options into image options, so the editor shows an
  // upload slot on every one rather than hiding the affordance behind a hover.
  showOptionImages?: boolean;
  // Presentation only, and per question rather than per option since a grid
  // mixing captioned and uncaptioned tiles reads as a mistake.
  hideImageOptionLabels?: boolean;
  pricePoints?: number[]; // for gabor_granger questions: the price ladder, ascending
  currencyCode?: string; // for gabor_granger questions: ISO 4217, defaults to USD
  // for gabor_granger questions: lowest value on the 1-5 intent scale counted as
  // "would buy". Steers the ladder; analysis can re-cut the curve at another bar.
  acceptIntentThreshold?: number;
  // for gabor_granger questions: which rungs the random start may draw from.
  // Undefined means "all", so existing questions and API callers that omit it
  // keep the current behaviour.
  startPriceRange?: GaborGrangerStartPriceRange;
  screenObservationEnabled?: boolean;
  // for file_upload questions: reference material the upload is checked
  // against. Never served to respondents (see stripFileUploadVerification).
  verification?: FileUploadVerificationConfig;
  task?: TaskConfig;
};

export type FileUploadVerificationConfig = {
  // What a matching upload must show, and how to read the reference images.
  // Required: images alone do not verify (the editor stores an empty string
  // while the researcher is still filling the popover).
  instructions: string;
  // url is a public URL in the study media bucket; the analyzer refuses others.
  referenceImages: { name: string; url: string }[];
};
// Each reference is another image in the model request.
export const MAX_FILE_UPLOAD_REFERENCE_IMAGES = 5;
export const EMPTY_FILE_UPLOAD_VERIFICATION: FileUploadVerificationConfig = {
  instructions: "",
  referenceImages: [],
};

export const FILE_UPLOAD_VERIFICATION_VERDICTS = [
  "match",
  "partial",
  "no_match",
  "unclear",
] as const;
export type FileUploadVerificationVerdict =
  (typeof FILE_UPLOAD_VERIFICATION_VERDICTS)[number];

// Allowed max_diff items-per-screen sizes; single source for the type, the
// editor control, the edit schema, and validation.
export const MAX_DIFF_PER_SCREEN_OPTIONS = [3, 4, 5] as const;
export type MaxDiffPerScreen = (typeof MAX_DIFF_PER_SCREEN_OPTIONS)[number];

// ############### Conjoint (CBC) ###############

// Authored order is the preference scale, so `ascending` puts the best level
// last (720p < 1080p < 4K) and `descending` first. Undefined means unordered.
export type ConjointPreferenceDirection = "ascending" | "descending";

// `dummy` gives every level its own part-worth; `linear` fits one slope over
// numeric level text, which sharpens WTP but needs every level numeric.
export type ConjointCodingType = "dummy" | "linear";

// One attribute (e.g. "Price") with its discrete levels. Levels are `Option`s
// so each level carries a stable global `OptionId`, mirroring max_diff choices.
export type ConjointAttribute = {
  id: ConjointAttributeId;
  name: string;
  levels: Option[];
  preferenceDirection?: ConjointPreferenceDirection;
  codingType?: ConjointCodingType;
};

// A single configured product: exactly one chosen level per attribute. Stored
// ordered by attribute for deterministic serialization/equality.
export type ConjointProfile = {
  levels: { attributeId: ConjointAttributeId; levelId: OptionId }[];
};

// One choice task = the profiles shown together on a single screen.
export type ConjointTask = {
  taskIndex: number;
  profiles: ConjointProfile[];
};

// Per-respondent conjoint design: the sequence of choice tasks they will see.
// Built once at interview start and persisted on interviewSampleState.
export type ConjointDesign = {
  tasks: ConjointTask[];
};

// `chosenProfileIndex` indexes `profiles`, -1 is "none of these", undefined is
// unanswered and stays distinct so a fresh task is not read as a none pick.
export type ConjointTaskState = ConjointTask & {
  chosenProfileIndex?: number;
};
// How a matrix is presented; "card_sort" drags rows as cards into columns as
// groups. The stored answer shape is identical, so capture onward treats both alike.
export const MATRIX_DISPLAY_OPTIONS = ["grid", "card_sort"] as const;
export type MatrixDisplay = (typeof MATRIX_DISPLAY_OPTIONS)[number];

// ----------- Other Interview Items ----------------

export type InterviewEndItem = InterviewItemTemplateBase & {
  type: "interview_end";
  redirect_url?: string;
  redirect_data_def?: string;
  not_add_existing_url_params?: boolean; // usually we add all existing url params to the redirect url
  // Translated config.redirect_submit_text_override; only language overlays set it.
  redirect_submit_text?: string;
};

export type IdentificationCollection = InterviewItemTemplateBase & {
  type: "identification_collection";
};

export type StatementItem = InterviewItemTemplateBase & {
  type: "statement";
};

export type VideoQualityCheckItem = InterviewItemTemplateBase & {
  type: "video_quality_check";
};

// custom_text / name / email_address all render as a single-line text input;
// name and email_address carry a default (still editable) label and email_address
// additionally validates that the value looks like an email. checkbox is a
// consent-style box. "text" was renamed to "custom_text" when the typed variants
// were added.
export type FormFieldType =
  | "custom_text"
  | "name"
  | "email_address"
  | "checkbox";

// Stable per-field identity (uuid), assigned at field creation. Lets fields be
// reordered without reinterpreting submitted values, and keys submission_data.
export type FormFieldId = string & { readonly __brand: unique symbol };

// A field is identified by its stable `id`. `label` is what the participant
// sees; field order is owned by the item's `fields` array (and snapshotted into
// submission_data.presented), so reordering never reinterprets a submission.
// The editor-only readable-id handle lives on CreationStateFormField
// (edit-chat/types.ts), not here.
export type FormFieldDef = {
  id: FormFieldId;
  label: string;
  type: FormFieldType;
  required: boolean;
};

// Submitted values keyed by field id: strings for text fields, booleans for
// checkboxes. A field absent from the map is unanswered. Vaulted in the
// form_submissions table, never written to history.
export type FormQuestionValues = Record<string, string | boolean>;

// "success" is the completed form; "incomplete" is the server-computed status
// when a required field is unsatisfied (the UI blocks submitting one, but a
// direct hit to /api/form-submission can still produce it).
// A "reject" state may be added in the future for decline-able forms.
export type FormSubmissionOutcome = "success" | "incomplete";

// History marker that a form was filled; the payload lives in the
// form_submissions table, pointed at by formSubmissionId.
export type AStateFormSubmission = {
  formSubmissionId?: FormSubmissionId;
  data?: FormQuestionValues;
  submissionStatus: FormSubmissionOutcome;
};

// A fixed-field form rendered verbatim (no LLM). Values are vaulted in the
// form_submissions table; history carries only a status marker + field labels.
// `text` is the intro paragraph.
// TODO(future, survey-public-form-question): public forms would inline values
// into history instead of writing a row, gated by a visibility flag threaded
// through the schema + converters the way `fields` is.
export type FormQuestionItem = InterviewItemTemplateBase & {
  type: "form_question";
  fields: FormFieldDef[];
};

export type UnsampledConceptBlockItem = InterviewItemTemplateBase & {
  type: "unsampled_concept_block";
  progressWeight: number; // expected weight on the progress bar
};

export type InterviewItemTemplate = UnionWithUndefined<
  [
    IdentificationCollection,
    QuestionItem,
    InterviewEndItem,
    StatementItem,
    VideoQualityCheckItem,
    UnsampledConceptBlockItem,
    FormQuestionItem,
  ]
>;

// ############### Screener ###############

/**
 * For multiple choice questions (single select) indicies of the approve/reject options are listed
 * and all options must be in either approve or reject.
 *
 * For multi-select questions, if the user selects any option in the reject list, they are screened out.
 * If they select at least one option in the approve list, they pass. If they only select options that
 * are not in approve or reject, they are approved.
 */
export type ScreenerQuestionRule = {
  approveOptions: OptionId[];
  rejectOptions: OptionId[];
  interview_question_item: InterviewItemTemplateId;
};

export type ScreenerData = {
  videoQualityCheckItem?: VideoQualityCheckItem;
  screeningEndItem: InterviewEndItem;
  screeningCriteria: ScreenerQuestionRule[];
  // calls backend for some scripted logic or quotas
  customCheck?: boolean;
};

// ############### Concept ###############

export type Concept = {
  id: ConceptId;
  nickname: string; // interal
  content: {
    title?: string;
    description?: string;
    media?: MediaDef[];
    embed?: {
      url: string;
      proxyUrl?: string;
      watermark?: boolean;
      byLanguage?: Partial<Record<AudioLanguage, EmbedVariant>>;
    };
  };
  // @experimental!
  conditional?: ConditionalDef; // this conditional will be transferred to the item
};

// ############### InterviewBlock ###############

export type InterviewFlatBlock = {
  type: "flat";
  id: InterviewBlockId;
  title?: string;
  items: InterviewItemTemplate[];
  conditional?: ConditionalDef;
};

export type ConceptSamplingConfig = {
  subsampleCount?: number;
  limitPerConcept?: number;
  onlySubset?: ConceptId[];
  conceptSamplingFunctionId?: ConceptSamplingFunction["id"];
};
export type InterviewConceptBlock = {
  type: "concept";
  id: InterviewBlockId;
  title?: string;
  items: InterviewItemTemplate[];
  samplingConfig: ConceptSamplingConfig;
  conditional?: ConditionalDef;
};

export type InterviewScreeningBlock = {
  type: "screening";
  id: InterviewBlockId;
  title?: string;
  items: InterviewItemTemplate[];
};

export type InterviewBlock =
  | InterviewFlatBlock
  | InterviewConceptBlock
  | InterviewScreeningBlock;

// --------------------------------

// we generate a list of those out of the interview block list
// those might also be re-ordered etc
export type InterviewItem = DistributiveOmit<
  InterviewItemTemplate,
  "id" | "media" | "choices"
> & {
  id: InterviewItemId;
  templateId: InterviewItemTemplateId;
  blockId: InterviewBlockId;
  conceptId: ConceptId | null;
  media?: MediaDef[]; // no placeholders
  // Runtime provenance: the task URL came from its sampled concept, not an override.
  isTaskUrlFromConcept?: boolean;
  /*
   Overide of interview item template choices
   this is post-resolving the choices for carry-forward questions
   so this is safe to access.
  */
  choices?: Option[];
};

// ############### InterviewState History ###############
export type InputType = "text" | "voice" | "button" | "jump";
export type InterviewStatus = "loading" | "typing" | "answering";

export type QState = {
  assMsg: string;
  // Pre-redaction snapshot of `assMsg`, set once by the first redacting pass; never shown to users
  assMsgBeforeRedaction?: string;
  additionalData?: string;
  currentItemId: InterviewItemId;
  currentTemplateId: InterviewItemTemplateId;
  currentConceptId: ConceptId | null;
  edited: boolean;
  assMsg_transl?: string;
};
export type MaxDiffDesign = {
  // Items to show on each screen.
  screens: OptionId[][];
};

export type MaxDiffScreen = {
  screenIndex: number;
  itemsShown: OptionId[];
  bestPick?: OptionId;
  worstPick?: OptionId;
};

// Which rungs the per-respondent random start may land on. Narrowing it trades a
// little extra ladder length for a tighter anchoring spread.
export type GaborGrangerStartPriceRange = "all" | "middle";

export type GaborGrangerDesign = {
  // Index into the question's `pricePoints` this respondent starts at.
  // Randomized per respondent to spread the anchoring the ladder introduces.
  startIndex: number;
};

export type GaborGrangerAnswer = {
  priceIndex: number;
  accepted: boolean;
};

export type GaborGrangerLadderState = {
  // Highest accepted price index, -1 when none were accepted.
  maxAccepted: number;
  // Lowest declined price index, `priceCount` when none were declined.
  minDeclined: number;
};

export type GaborGrangerRung = {
  rungIndex: number;
  priceIndex: number;
  price: number;
  // Raw intent selection, kept unthresholded so analysis can re-cut the demand
  // curve at a different acceptance bar than the one used to steer the ladder.
  intentValue?: number;
  accepted?: boolean;
};

export type ResponseInterviewFileRef = {
  id: string;
  displayName: string;
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
};

export type ResponseInterviewFileUpload = {
  fileName: string;
  fileSize: number;
  mimeType: string;
  /** Present after a successful direct upload to Supabase Storage (live responses). */
  fileRef?: ResponseInterviewFileRef;
  /** LLM-generated summary of the uploaded file, cached on the history entry. */
  fileUploadDescription?: string;
  /** Set when the question carries a verification config and analysis ran. */
  fileUploadVerification?: ResponseInterviewFileVerification;
};

export type ResponseInterviewFileVerification = {
  verdict: FileUploadVerificationVerdict;
  reasoning: string;
};

export type AState = {
  selectedOptionIds: OptionId[];
  inputType: InputType;
  input: string;
  voiceInput: string; // voice input before rewriting but without []
  rawInput: string; // what the LLM sees, ie options stringified, with [] from voice etc
  language?: AudioLanguage;
  input_transl?: string;
  rewriteSimilarity?: number;
  rewriteRejected?: boolean;
  voiceInputLive?: string;
  rawInputLive?: string;
  // Pre-labeling snapshot of `input`, set only when speaker labeling rewrote
  // `input` with `<name>: ...` markers. Lets re-runs stay idempotent.
  inputBeforeSpeakerLabeling?: string;
  inputBeforeRedaction?: string;
  // Pre-processing snapshot of `input`, set only when language-specific text
  // processing (e.g. zh-TW simplified -> traditional) actually changed `input`.
  inputBeforeTextProcessing?: string;
  // Set on manual edit of `input` / `input_transl`. Post-processing must not
  // regenerate a manually edited field.
  inputManuallyEditedAt?: string;
  inputTranslManuallyEditedAt?: string;
  // Max diff: per-screen metadata. Present on every max_diff screen entry.
  maxDiffScreen?: MaxDiffScreen;
  // Conjoint: per-task metadata. Present on every conjoint choice-task entry.
  conjointTask?: ConjointTaskState;
  // Gabor granger: per-rung metadata. Present on every gabor_granger rung entry.
  gaborGrangerRung?: GaborGrangerRung;
  screenRecordingDescription?: string;
  /** Set when the respondent selected a file (file_upload questions). */
  fileUpload?: ResponseInterviewFileUpload;
  /** Set when this entry answers a form_question item. */
  formSubmission?: AStateFormSubmission;
};

export type FraudStatusEntry = {
  copy_partial?: boolean; // respondent copied a part of the question
  copy_full?: boolean; // respondent copied the entire question
  focus_switch?: boolean; // respondent switched tabs back and forth
};

export type QAState = QState &
  AState & {
    redacted?: boolean;
    fraudStatus?: FraudStatusEntry;
    recordings?: ResponseRecording[];
    recordingDiagnostics?: RecordingDiagnostics;
    submittedAt?: string;
    skipped?: boolean; // preview skips, or a participant skipping an isSkippable question
  };
export type QAStateWithoutMessageText = Omit<
  QAState,
  | "rawInput"
  | "input"
  | "voiceInput"
  | "input_transl"
  | "assMsg"
  | "assMsg_transl"
  | "voiceInputLive"
  | "rawInputLive"
  | "inputBeforeSpeakerLabeling"
  | "inputBeforeRedaction"
  | "inputBeforeTextProcessing"
  | "assMsgBeforeRedaction"
>;

// ----------------------------------------

export type DynamicIncentiveArm = "treatment" | "control";

// Set once at response creation; see components/interview/dynamicIncentive.ts.
export type DynamicIncentiveState = {
  arm: DynamicIncentiveArm;
  amountUsd: number;
  version: 1;
};

export type InterviewSampleState = {
  // for each block, which concepts were sampled and in which order
  conceptsByBlock: Record<InterviewBlockId, ConceptId[]>;
  dynamicIncentive?: DynamicIncentiveState;
  // for each sampled concrete max-diff item, which item IDs appear on each screen
  maxDiffDesignsByItem?: Record<InterviewItemId, MaxDiffDesign>;
  // for each sampled conjoint item, the per-respondent sequence of choice tasks
  conjointDesignsByItem?: Record<InterviewItemId, ConjointDesign>;
  // for each sampled concrete gabor-granger item, where on the price ladder this
  // respondent starts
  gaborGrangerDesignsByItem?: Record<InterviewItemId, GaborGrangerDesign>;
};

export type InterviewBlockConceptSamplingCount = Record<ConceptId, number>;

export type InterviewConceptSamplingCount = Record<
  InterviewBlockId,
  InterviewBlockConceptSamplingCount
>;

// ########################################################
// ############### specific types for props ###############

export type InterviewRef = {
  restart_session: (
    startItemTemplateId: InterviewItemTemplateId | undefined,
    options?: {
      searchParamOverride?: Record<string, string>;
      clear?: boolean;
      screenerStatus?: ScreenerStatusFull;
    }
  ) => void;
  setWelcomeAndDisclosureCompleted: (completed: boolean) => void;
  screenOut: (reason: RejectReason) => void;
};

type DeviceHandlers = {
  setDevice: (device: InputDeviceInfo) => Promise<void>;
  getDevices: () => Promise<InputDeviceInfo[]>;
  startDevice: () => Promise<{ success: boolean }>;
  stopDevice: () => void;
};

export type DeviceState = DeviceHandlers & {
  state: "inactive" | "starting" | "active";
  recordingId?: string;
};

export type AudioState = DeviceState & {
  micName: string;
  volume: number;
};

export type VideoState = DeviceState & {
  isRearVideo?: boolean;
  videoStream: MediaStream | null;
};

export type RecordingHandlers = {
  startDevices: () => void;
  stopDevices: () => void;
  abortRecording: () => void;
  startRecording: (props: { delayed: boolean }) => void;
  stopRecording: (props: {
    save: boolean;
    requireTranscript?: boolean;
  }) => Promise<void>;
  retryUploads: () => Promise<void>;
};

export type RecordingState = {
  state:
    | "standby"
    | "recording"
    | "transcribing"
    | "starting_mobile_screen_recording"
    | "failed_to_start_recording";
  start: Date;
  duration: number;
  numUploadsRunning: number;
  numUploadsFailed: number;
  audio: AudioState;
  video: VideoState;
  screen: VideoState;
};

type AudioAction =
  | { type: "device_ready"; deviceType: "audio" }
  | { type: "set_mic_name"; micName: string }
  | { type: "transcription_failed" | "final_transcript" };
type VideoAction = {
  type: "device_ready";
  deviceType: "video" | "screen";
  videoStream: MediaStream | null;
  isRearVideo?: boolean;
};

export const CAPTURE_DEVICE_TYPES = ["audio", "video", "screen"] as const;
export type CaptureDevice = (typeof CAPTURE_DEVICE_TYPES)[number];

export type StateAction =
  | AudioAction
  | VideoAction
  | {
      type: "start_device" | "reset_device";
      deviceType: CaptureDevice;
    }
  | {
      type: "start_recording";
      deviceType: CaptureDevice;
      recordingStart: Date;
      recordingId: string;
      skipStateChange?: boolean;
    }
  | { type: "stop_recording"; recordingDuration: number }
  | { type: "abort_recording" }
  | { type: "cancel_start_recording" }
  | { type: "start_upload_recording"; isRetry: boolean }
  | { type: "finish_upload_recording"; shouldRetry: boolean }
  | { type: "start_mobile_screen_recording" };

export type RecordingIdsAndSecret = {
  recordingId: string;
  response_id: ResponseId;
  response_secret: string;
};

export type UploadResult =
  | {
      shouldRetry: false;
    }
  | {
      shouldRetry: true;
      retry_function: () => Promise<boolean>;
    };

// reasons for not being able to proceed to next question
// currently used to force switching to embed view
export type SubmitBlock = {
  blocked: true;
  reason: string;
  submitTried: () => void;
};

export type InterviewUIProps = {
  jump: (i: number) => void;
  onSubmit: (
    input: string,
    aStateUpt?: Partial<AState>,
    options?: { forceSkipLlm?: boolean }
  ) => void;
  onSkip: () => void;
  // Submits raw field values; resolves once the form_submissions commit settles.
  onSubmitFormQuestion: (values: FormQuestionValues) => Promise<void>;
  onPressOption: ({
    optionId,
    isMultiSelect,
  }: {
    optionId: OptionId;
    isMultiSelect: boolean;
  }) => void;
  setRankingOptions: (optionIds: OptionId[]) => void;
  config: SurveyConfig;
  isEditMode: boolean;
  survey_id: SurveyId;
  org_id?: OrganizationId;
  response_id: ResponseId | undefined;
  session_secret: string;
  language: AudioLanguage;
  setLanguage: (l: AudioLanguage) => void;
  status: InterviewStatus;
  recordingState: RecordingState;
  dispatchRecordingEvent: (event: RecordingEvent) => Promise<void>;
  // tracks whether the user currently has voice input active in AnswerInput,
  // so logic outside the component (e.g. transcript handling) can react to it
  useVoiceRef: MutableRefObject<boolean>;
  currentQuestionInfo: CurrentQuestionInfo;
  historyInfo: HistoryInfo;
  forceMediaPlayingInfo: ForceMediaPlayingInfo;
  screenerStatus: ScreenerStatus;
  screenerItems: InterviewItem[];
  maxDiffDesignsByItem?: Record<InterviewItemId, MaxDiffDesign>;
  conjointDesignsByItem?: Record<InterviewItemId, ConjointDesign>;
  gaborGrangerDesignsByItem?: Record<InterviewItemId, GaborGrangerDesign>;
  canAnswer: boolean;
  isMCFollowUp: boolean;
  hasUnpublishedChanges: boolean;
  translationsUnavailable: boolean;
  translationsLoading?: boolean;
  video_quality_check_attempts?: number;
  templateNumbers: Map<InterviewItemTemplateId, number>;
  autorecord: boolean;

  // only set in the embed wrapper
  hasEmbed?: boolean;
  submitBlocked?: SubmitBlock;
  interviewerHidden?: boolean;
  isPanelParticipant?: boolean;
  getRedirectInfo?: () => RedirectInfo | null;
};

export type SendMessageProps = {
  newHistory: QAState[];
  useVoice: boolean;
  startItemTemplateId?: InterviewItemTemplateId;
  newScreenerStatus: ScreenerStatusFull;
  forceStartFromOffset?: boolean;
  screenRecordingDescription?: string;
  fileUploadDescription?: string;
};

export type InterviewIds = {
  survey_id: SurveyId;
  response_id: ResponseId;
  user_id: UserId;
  revision_id: SurveyRevisionId;
};

export type VideoType = Database["public"]["Enums"]["video_type"];

export const RECORDING_ERROR_MESSAGES = {
  FAILED_TO_MARK_CHUNK_START: "Failed to mark chunk start",
  RECORDING_ABORTED_DUE_TO_INIT_FAILURE:
    "Recording aborted due to init failure",
} as const;

// What the transcription internals produce; they don't know which answer the
// audio belongs to, so the recorder layer adds historyIndex before forwarding.
export type FinalTranscriptPayload =
  | {
      transcript: string;
      // null when the realtime provider doesn't return per-word data.
      words: TranscriptionWord[] | null;
    }
  // TODO(soon): have the mobile app also send words
  | {
      transcript: string;
      cleanTranscript: string;
    };

export type FinalTranscriptArgs = FinalTranscriptPayload & {
  // Captured by the recorder when the answer's recording started; matches
  // audio_responses.response_history_index for the same audio. Late
  // transcripts must be attributed to this entry, not the current one.
  historyIndex: number;
};
